import React, { ReactNode } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Button, Paper } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import { CheckResult, ObjectChecked, ObjectCheckFailureSummary, Variable } from '@paragon/contract-library';
import { hasAnalysisEvaluationPermission } from '@paragon/login-tools';
import { CircularProgressWithLabel } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { getGroupingMap, humanCase, makeKeyByCache } from '@paragon/utilities';

import { getColorHexStringFromCapacityRatio, passingNonLoadCapacityCheckColor } from '../../../capacityColors';
import { isPassingNonLoadCapacityCheck } from '../../../library/isPassingNonLoadCapacityCheck';
import { RootState } from '../../../store';
import { loadActiveAnalysisResults } from '../../../store/actions/loadAnalysisResults';
import { getActiveAnalysisSet } from '../../../store/selectors/getActiveAnalysisSetSelector';
import { getPossibleAnalysisResultRequestsForActiveLoadCase } from '../../../store/selectors/getAnalysisResultRequestForActiveLoadCaseSelector';
import {
  getAnalysisResultGuids,
  getAnalysisResultRequestsForActiveAnalysisSet,
} from '../../../store/selectors/getAnalysisResultsForActiveAnalysisSetSelector';
import {
  CheckResultTreeEntry,
  getCheckResultTreeEntries,
} from '../../../store/selectors/getCheckResultTreeEntriesSelector';
import { checkResultPanelExpandedItemIdsSelect } from '../../../store/slices/panelDataSlice';

interface Item {
  readonly itemId: string;
  readonly label: ReactNode;
  readonly children?: readonly Item[];
}

interface CheckResultItem extends Item {
  readonly type: 'checkResult';
  readonly checkResult: CheckResult | null | undefined;
  readonly capacity: number;
}

interface CheckResultGroupItem extends Item {
  readonly treeEntries: readonly CheckResultTreeEntry[];
  readonly shouldColorAsPassingNonLoadCapacityCheck?: boolean;
  readonly checkFailureSummary?: string;
}

// A function that can group check results by a specific piece of data, producing data items for each group.  Returns
//   null if there should not be a tree layer for the data selected by this ChildExtractor in this subtree.
type ChildExtractor = (treeEntries: readonly CheckResultTreeEntry[]) => CheckResultGroupItem[] | null;

const checkGroupChildExtractor: ChildExtractor = (treeEntries) =>
  (treeEntries[0].check || null) &&
  Array.from(
    getGroupingMap(treeEntries, (treeEntry) => treeEntry.check!.name),
    ([name, group]) => ({
      itemId: name,
      label: `${group[0].check!.tpiSection}: ${name}`,
      treeEntries: group,
      shouldColorAsPassingNonLoadCapacityCheck: group.every(({ check, capacity }) =>
        isPassingNonLoadCapacityCheck({ name: check!.name, capacity }),
      ),
    }),
  );

const getCheckedObject = (treeEntry: CheckResultTreeEntry, subObjectIndex: number) =>
  (subObjectIndex < 0 ? treeEntry.mainObject : treeEntry.subObjects?.[subObjectIndex]) || null;

const getMainObjectCategory = (mainObject: ObjectChecked) => {
  switch (mainObject.type) {
    case 'Plate Pair':
      return 'Plates';
    case 'Joint':
      return 'Joints';
    default:
      return 'Members';
  }
};

const mainObjectTypeChildExtractor: ChildExtractor = (treeEntries) =>
  (treeEntries[0]?.mainObject || null) &&
  Array.from(
    getGroupingMap(
      treeEntries.map((treeEntry) => ({ treeEntry, mainObjectCategory: getMainObjectCategory(treeEntry.mainObject!) })),
      ({ mainObjectCategory }) => mainObjectCategory,
    ),
    ([mainObjectCategory, group]) => ({
      itemId: mainObjectCategory,
      label: mainObjectCategory,
      treeEntries: group.map(({ treeEntry }) => treeEntry),
    }),
  );

const loadCaseChildExtractor: ChildExtractor = (treeEntries) =>
  (treeEntries[0]?.loadCase || null) &&
  Array.from(
    getGroupingMap(treeEntries, (treeEntry) => treeEntry.loadCase),
    ([loadCase, group]) => ({
      itemId: `${loadCase!.index}`,
      label: `Load Case ${loadCase!.index}. ${loadCase!.name}`,
      treeEntries: group,
    }),
  );

const plateMislocationChildExtractor: ChildExtractor = (treeEntries) =>
  treeEntries[0].plateMislocation &&
  Array.from(
    getGroupingMap(
      treeEntries.map((treeEntry) => ({ treeEntry, plateMislocation: treeEntry.plateMislocation! })),
      ({ plateMislocation }) => plateMislocation.guid,
    ),
    ([plateMislocationGuid, group]) => ({
      itemId: plateMislocationGuid,
      label: `Plate Mislocation ${group[0].plateMislocation.name}`,
      treeEntries: group.map(({ treeEntry }) => treeEntry),
    }),
  );

function isCheckResultItem(item: Item): item is CheckResultItem {
  return (item as CheckResultItem).type === 'checkResult';
}

const renderFakeTreeDepth = (parentItemId: string, noChildren: boolean): readonly Item[] =>
  noChildren ? [] : [{ itemId: `${parentItemId}-dummy`, label: '' }];

const renderVariable = (variable: Variable, itemId: string): Item => {
  const label = (
    <>
      <Box component="span" sx={{ color: '#ce93eb' }}>{`${variable.name}: `}</Box>
      <Box component="span" sx={{ color: 'white' }}>
        {variable.value}
      </Box>
    </>
  );

  const children = variable.children?.map((variable, index) => renderVariable(variable, `${itemId}-${index}`)) ?? [];

  return {
    itemId,
    label,
    children,
  };
};

const analysisResultLoadingErrorMessage = 'Error loading analysis details. Click to retry.';

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

class CheckResultsPanel extends React.Component<Props> {
  filterItems(items: readonly Item[], callbackFn: (item: Item) => boolean): readonly Item[] {
    const filteredItems: Item[] = [];
    for (const item of items) {
      if (callbackFn(item)) filteredItems.push(item);
      if (item.children) filteredItems.push(...this.filterItems(item.children, callbackFn));
    }
    return filteredItems;
  }

  onExpandedItemsChange = (_: React.SyntheticEvent | null, itemIds: string[]) => {
    if (itemIds.length > 0) {
      this.props.loadAnalysisResults();
    }
    this.props.setCheckResultExpandedItemIds(itemIds);
  };

  // Generates items for each distinct value of treeEntry.subObjects[subObjectIndex] (if subObjectIndex >= 0) or for each
  // distinct value of treeEntry.mainObject otherwise.
  readonly getCheckedObjectChildExtractor =
    (subObjectIndex = -1): ChildExtractor =>
    (treeEntries) =>
      ((treeEntries[0] && getCheckedObject(treeEntries[0], subObjectIndex)) || null) &&
      Array.from(
        getGroupingMap(
          treeEntries.map((treeEntry) => ({ treeEntry, checkedObject: getCheckedObject(treeEntry, subObjectIndex)! })),
          ({ checkedObject }) => checkedObject.guid,
        ),
        ([checkedObjectGuid, group]) => {
          const { checkedObject } = group[0];
          const { objectCheckFailureSummariesByGuid } = this.props;

          const checkFailureSummary =
            (subObjectIndex < 0 &&
              objectCheckFailureSummariesByGuid &&
              objectCheckFailureSummariesByGuid[checkedObject.guid]) ||
            null;

          return {
            itemId: checkedObjectGuid,
            label: `${humanCase(checkedObject.type)} ${checkedObject.name}`,
            treeEntries: group.map(({ treeEntry }) => treeEntry),
            checkFailureSummary: checkFailureSummary?.checkFailureSummary,
          };
        },
      );

  readonly childExtractors: readonly ChildExtractor[] = [
    mainObjectTypeChildExtractor,
    this.getCheckedObjectChildExtractor(),
    checkGroupChildExtractor,
    this.getCheckedObjectChildExtractor(0),
    this.getCheckedObjectChildExtractor(1),
    loadCaseChildExtractor,
    plateMislocationChildExtractor,
  ];
  readonly checkGroupChildExtractorIndex = this.childExtractors.indexOf(checkGroupChildExtractor);

  callNextChildExtractor(treeEntries: readonly CheckResultTreeEntry[], childExtractorStartIndex: number) {
    let childExtractorIndex = childExtractorStartIndex;
    for (; childExtractorIndex < this.childExtractors.length; childExtractorIndex += 1) {
      const childrenData = this.childExtractors[childExtractorIndex](treeEntries);
      if (childrenData) {
        return { childrenData, childExtractorIndex };
      }
    }
    return { childrenData: null, childExtractorIndex };
  }

  getChildRenderers(
    treeEntries: readonly CheckResultTreeEntry[],
    childExtractorStartIndex: number,
    parentItemId: string,
  ): readonly (() => Item)[] {
    const { childrenData, childExtractorIndex } = this.callNextChildExtractor(treeEntries, childExtractorStartIndex);
    if (!childrenData) {
      console.assert(treeEntries.length === 1);
      if (!treeEntries[0].checkResult) {
        const label =
          this.props.analysisResultsLoadingStatus === 'error' ? (
            <div onClick={() => void this.props.loadAnalysisResults()}>
              <InlineNotification type="error">{analysisResultLoadingErrorMessage}</InlineNotification>
            </div>
          ) : this.props.analysisResultsLoadingStatus === 'loading' ? (
            <CircularProgressWithLabel value={this.props.analysisResultsLoadedPercentage} />
          ) : this.props.analysisResultsLoadingStatus === 'resultsNotSaved' ? null : (
            <Button onClick={() => void this.props.loadAnalysisResults()} variant="outlined">
              Load Analysis Details
            </Button>
          );
        return label ? [() => ({ itemId: `${parentItemId}-spinner`, label })] : [];
      }
      return (
        (this.props.hasAnalysisEvaluationPermission &&
          treeEntries[0].checkResult.variables?.map(
            (variable, index) => () => renderVariable(variable, `${parentItemId}-${index}`),
          )) ||
        []
      );
    }

    return childrenData.map((itemData) => () => {
      const itemId = `${parentItemId}~${itemData.itemId}`;
      const highestTreeEntry = itemData.treeEntries.find(
        (treeEntry) =>
          !treeEntry.checkResult ||
          !isPassingNonLoadCapacityCheck(treeEntry.checkResult) ||
          childExtractorIndex >= this.checkGroupChildExtractorIndex,
      );
      const greatestCapacity = highestTreeEntry?.capacity ?? 0;
      const color = itemData.shouldColorAsPassingNonLoadCapacityCheck
        ? passingNonLoadCapacityCheckColor
        : getColorHexStringFromCapacityRatio(greatestCapacity);
      const children = this.renderChildren(itemData.treeEntries ?? [], childExtractorIndex + 1, itemId);

      const checkFailureSummaryString = itemData.checkFailureSummary ? ` (${itemData.checkFailureSummary})` : '';

      return {
        itemId,
        label: (
          <Box component="span" sx={{ color }}>{`${itemData.label} ${Math.round(
            greatestCapacity * 100,
          )}%${checkFailureSummaryString}`}</Box>
        ),

        type: 'checkResult',
        checkResult: highestTreeEntry?.checkResult,
        children,
        capacity: greatestCapacity,
      };
    });
  }

  renderChildren(
    treeEntries: readonly CheckResultTreeEntry[],
    childExtractorStartIndex: number,
    parentItemId: string,
  ): readonly Item[] | undefined {
    if (treeEntries.length === 0) return [];

    const childRenderers = this.getChildRenderers(treeEntries, childExtractorStartIndex, parentItemId);
    if (!this.props.expandedItemIds.includes(parentItemId) && parentItemId !== '') {
      return renderFakeTreeDepth(parentItemId, !childRenderers.length);
    }

    return childRenderers
      .map((childRenderer) => childRenderer())
      .toSorted((a, b) => (isCheckResultItem(b) ? b.capacity : 0) - (isCheckResultItem(a) ? a.capacity : 0));
  }

  renderItems(items: readonly Item[] | undefined) {
    return (
      items?.map((item) => (
        <TreeItem key={item.itemId} itemId={item.itemId} label={item.label}>
          {this.renderItems(item.children)}
        </TreeItem>
      )) ?? null
    );
  }

  renderTree() {
    const { treeEntries, expandedItemIds } = this.props;
    const items = treeEntries ? this.renderChildren(treeEntries, 0, '') : undefined;

    return (
      <SimpleTreeView
        multiSelect
        expansionTrigger="iconContainer"
        expandedItems={
          expandedItemIds as string[] /* FIXME Submit a PR to MUI to make the prop accept a readonly array */
        }
        onExpandedItemsChange={this.onExpandedItemsChange}
      >
        {this.renderItems(items)}
      </SimpleTreeView>
    );
  }

  renderPanelContents() {
    if (this.props.treeEntries) {
      return this.renderTree();
    }
    switch (this.props.analysisResultsLoadingStatus) {
      case 'loading':
        return (
          <div style={{ textAlign: 'center' }}>
            <CircularProgressWithLabel value={this.props.analysisResultsLoadedPercentage} />
          </div>
        );
      case 'error':
        return (
          <div onClick={this.props.loadAnalysisResults}>
            <InlineNotification type="error">{analysisResultLoadingErrorMessage}</InlineNotification>
          </div>
        );
      case 'canBeLoaded':
        return (
          <div>
            <Button onClick={this.props.loadAnalysisResults} variant="outlined">
              Load Analysis Details
            </Button>
          </div>
        );
      case 'resultsNotSaved':
        return (
          <div>
            <InlineNotification type="warning">
              Run a detailed analysis to get details for individual load cases.
            </InlineNotification>
          </div>
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <Paper variant="outlined" elevation={0} id="checkResultsPanel" sx={{ p: 1 }}>
        {this.renderPanelContents()}
        {this.props.analysisResultsLoadingStatus === 'resultsNotSaved' && this.props.treeEntries && (
          <div>
            <InlineNotification type="warning">
              Run a detailed analysis to get details for individual checks.
            </InlineNotification>
          </div>
        )}
      </Paper>
    );
  }
}

const getObjectCheckFailureSummariesByGuid = makeKeyByCache(
  (objectCheckFailureSummary: ObjectCheckFailureSummary) => objectCheckFailureSummary.objectChecked.guid,
);

const mapStateToProps = (state: RootState) => {
  const { selectedCheckResultsLoadCaseGuid } = state.selections;
  let analysisResultsLoadingStatus: 'loaded' | 'loading' | 'error' | 'canBeLoaded' | 'resultsNotSaved' | null;
  const analysisSet = getActiveAnalysisSet(state);

  let statuses;
  if (analysisSet) {
    if (getAnalysisResultGuids(analysisSet)) {
      if (selectedCheckResultsLoadCaseGuid) {
        statuses = getPossibleAnalysisResultRequestsForActiveLoadCase(state)?.map((request) => request?.status ?? null);
        const statusesSet = new Set(statuses);

        analysisResultsLoadingStatus = statusesSet.has('loaded')
          ? 'loaded'
          : statusesSet.has(null)
            ? 'canBeLoaded'
            : statusesSet.has('loading')
              ? 'loading'
              : statusesSet.has('error')
                ? 'error'
                : null;
      } else {
        statuses = getAnalysisResultRequestsForActiveAnalysisSet(state)?.map((request) => request?.status ?? null);
        const statusesSet = new Set(statuses);

        analysisResultsLoadingStatus = statusesSet.has('error')
          ? 'error'
          : statusesSet.has(null)
            ? 'canBeLoaded'
            : statusesSet.has('loading')
              ? 'loading'
              : 'loaded';
      }
    } else {
      analysisResultsLoadingStatus = 'resultsNotSaved';
    }
  } else {
    analysisResultsLoadingStatus = null;
  }

  let analysisResultsLoadedPercentage = 0;
  if (statuses) {
    const loadingStatuses = statuses.filter((status) => status === 'loading');
    analysisResultsLoadedPercentage = ((statuses.length - loadingStatuses.length) / statuses.length) * 100;
  }

  const objectCheckFailureSummaries = analysisSet?.objectCheckFailureSummaries;

  return {
    hasAnalysisEvaluationPermission: hasAnalysisEvaluationPermission(state),
    expandedItemIds: state.panelData.checkResultExpandedItemIds,
    // issue here, selector gets recomposed every time selected item property changes
    treeEntries: getCheckResultTreeEntries(state),
    analysisResultsLoadingStatus,
    analysisResultsLoadedPercentage,
    objectCheckFailureSummariesByGuid:
      (objectCheckFailureSummaries && getObjectCheckFailureSummariesByGuid(objectCheckFailureSummaries)) ?? null,
  };
};

const actionCreators = {
  setCheckResultExpandedItemIds: checkResultPanelExpandedItemIdsSelect,
  loadAnalysisResults: loadActiveAnalysisResults,
};

export default connect(mapStateToProps, actionCreators)(CheckResultsPanel);
